import ReactECharts from 'echarts-for-react';
import industryProblems from '../../data/industryProblems.json';
import { useIsDark } from '../../useIsDark';

/**
 * ReliabilityGapChart — AI agents on benchmarks vs in production (blog14, Fig 2).
 *
 * Chart-type-first: two-group comparison → ECharts vertical bar. Data reads from
 * industryProblems.json (figures.reliability_gap). Per the Human Manifesto, this
 * chart compares agents to their own benchmark scores — never to humans. Brand
 * colors: benchmark = amber (warning), production = red (unreliable). Long
 * category labels wrap to a second row (no overlap). Theme: `dark` prop
 * override, otherwise follows the live html class theme (Rule 29).
 * ONE shared formatter.
 */

const BRAND = {
  warning: '#F59E0B',
  error: '#EF4444',
};

type GapItem = { label: string; value: number };

const formatPct = (v: number) => `${v.toFixed(0)}%`;

export const ReliabilityGapChart = ({
  height = 320,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const fig = (
    industryProblems as unknown as {
      figures: { reliability_gap: { source: string; note: string; items: GapItem[] } };
    }
  ).figures.reliability_gap;

  const C = isDark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { marker?: string; name: string; value: number }[]) =>
        params.map((p) => `${p.marker} ${p.name}: <b>${formatPct(p.value)}</b>`).join('<br/>'),
    },
    grid: { left: 52, right: 48, top: 40, bottom: 72 },
    xAxis: {
      type: 'category',
      data: fig.items.map((i) => i.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisLabel: {
        color: C.axis,
        fontSize: 12,
        fontWeight: 700,
        interval: 0,
        width: 110,
        overflow: 'break',
        lineHeight: 15,
      },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: 'success rate (%)',
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.35 } },
    },
    series: [
      {
        name: 'Success rate',
        type: 'bar',
        barWidth: '36%',
        data: fig.items.map((i) => ({
          value: i.value,
          itemStyle: {
            color: i.label.toLowerCase().includes('benchmark') ? BRAND.warning : BRAND.error,
            borderRadius: [6, 6, 0, 0],
          },
        })),
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontWeight: 700,
          fontSize: 13,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center`}>
        Figure 2. AI agents: benchmark vs production ({fig.source}). {fig.note}
      </p>
    </div>
  );
};
